/**
 * ROLES SERVICE - Base Architecture
 * 
 * Gestión de roles y permisos (Admin only)
 */

import { BaseService } from '../base.service';
import { db } from '@/core/config/firebase';
import { Collections } from '@/core/config/collections';
import { UserRole, Permission } from '../../types/enums';
import type { UserRoleData } from '../../types/models';
import { ChangeUserRoleSchema } from '../../utils/validation';
import { BusinessError, NotFoundError } from '../../utils/errors';


export class RolesService extends BaseService<UserRoleData> {
  constructor() {
    super('user_roles', db);
  }

  /**
   * Get role data for user
   */
  async getUserRole(userId: string): Promise<UserRoleData | null> {
    return this.getById(userId);
  }

  /**
   * Change user role (Admin only)
   */
  async changeUserRole(userId: string, newRole: UserRole, addRole = false): Promise<void> {
    this.logger.info('Changing user role', { userId, newRole, addRole });
    
    const currentRoleData = await this.getById(userId);

    if (!currentRoleData) {
      // Create new role data
      await this.create({
        userId,
        roles: [newRole],
        primaryRole: newRole,
        permissions: this.getPermissionsByRole(newRole),
        updatedAt: new Date().toISOString()
      } as any, userId);
      
      // Also update user document
      await db.collection(Collections.USERS).doc(userId).update({
        role: newRole,
        updatedAt: new Date().toISOString()
      });
      
      return;
    }

    // Update existing
    let newRoles: UserRole[];
    
    if (addRole) {
      // Add role to existing
      newRoles = currentRoleData.roles.includes(newRole)
        ? currentRoleData.roles as UserRole[]
        : [...currentRoleData.roles, newRole] as UserRole[];
    } else {
      // Replace all roles
      newRoles = [newRole];
    }

    const newPermissions = this.getPermissionsForRoles(newRoles);

    await this.update(userId, {
      roles: newRoles,
      primaryRole: newRole,
      permissions: newPermissions
    } as any);
    
    // Also update user document for backwards compatibility
    await db.collection(Collections.USERS).doc(userId).update({
      role: newRole,
      updatedAt: new Date().toISOString()
    });
    
    this.logger.info('User role changed successfully', { userId, newRole });
  }

  /**
   * Add role to user (multi-role support)
   */
  async addRole(userId: string, role: UserRole): Promise<void> {
    await this.changeUserRole(userId, role, true);
  }

  /**
   * Remove role from user
   */
  async removeRole(userId: string, role: UserRole): Promise<void> {
    const roleData = await this.assertExists(userId);

    if (roleData.roles.length === 1) {
      throw new BusinessError('Cannot remove last role from user');
    }

    const newRoles = roleData.roles.filter(r => r !== role) as UserRole[];
    const newPrimaryRole = newRoles[0];
    const newPermissions = this.getPermissionsForRoles(newRoles);

    await this.update(userId, {
      roles: newRoles,
      primaryRole: newPrimaryRole,
      permissions: newPermissions
    } as any);
  }

  /**
   * Get permissions for a single role
   */
  private getPermissionsByRole(role: UserRole): Permission[] {
    // Definir permisos base sin recursión
    const userPermissions = [
      Permission.CAN_CREATE_CIRCLES,
      Permission.CAN_JOIN_CIRCLES,
      Permission.CAN_POST_IN_CIRCLES
    ];
    
    const therapistPermissions = [
      Permission.CAN_CREATE_CIRCLES,
      Permission.CAN_JOIN_CIRCLES,
      Permission.CAN_POST_IN_CIRCLES,
      Permission.CAN_MANAGE_PATIENTS,
      Permission.CAN_CREATE_PROFESSIONAL_CIRCLES,
      Permission.CAN_ACCESS_ANALYTICS,
      Permission.CAN_GENERATE_CONTENT
    ];
    
    // Asistente: permisos limitados (puede ver pero no modificar)
    const assistantPermissions = [
      Permission.CAN_JOIN_CIRCLES,
      Permission.CAN_POST_IN_CIRCLES,
      Permission.CAN_ACCESS_ANALYTICS  // Solo lectura de analytics
      // NO puede: gestionar pacientes, crear círculos, generar contenido
    ];
    
    // School Admin: puede gestionar contenido y prácticas de la escuela
    const schoolAdminPermissions = [
      Permission.CAN_CREATE_CIRCLES,
      Permission.CAN_JOIN_CIRCLES,
      Permission.CAN_POST_IN_CIRCLES,
      Permission.CAN_MANAGE_CONTENT,
      Permission.CAN_ACCESS_ANALYTICS,
      Permission.CAN_GENERATE_CONTENT
    ];
    
    const adminPermissions = [
      Permission.CAN_MANAGE_USERS,
      Permission.CAN_MANAGE_ROLES,
      Permission.CAN_MANAGE_CONTENT,
      Permission.CAN_MODERATE_CIRCLES,
      Permission.CAN_VIEW_ALL_ANALYTICS,
      Permission.CAN_MANAGE_BILLING,
      // Admin también tiene todos los permisos de user y therapist
      ...userPermissions,
      ...therapistPermissions
    ];
    
    const permissionMap: Record<UserRole, Permission[]> = {
      [UserRole.USER]: userPermissions,
      [UserRole.THERAPIST]: therapistPermissions,
      [UserRole.THERAPIST_ASSISTANT]: assistantPermissions,
      [UserRole.SCHOOL_ADMIN]: schoolAdminPermissions,
      [UserRole.ADMIN]: adminPermissions
    };

    return [...new Set(permissionMap[role] || [])]; // Remove duplicates
  }

  /**
   * Get permissions for multiple roles (union)
   */
  private getPermissionsForRoles(roles: UserRole[]): Permission[] {
    const allPermissions = roles.flatMap(role => this.getPermissionsByRole(role));
    return [...new Set(allPermissions)]; // Remove duplicates
  }

  /**
   * Check if user has permission
   */
  async hasPermission(userId: string, permission: Permission): Promise<boolean> {
    const roleData = await this.getById(userId);
    if (!roleData) return false;
    return roleData.permissions.includes(permission);
  }

  /**
   * Check if user has role
   */
  async hasRole(userId: string, role: UserRole): Promise<boolean> {
    const roleData = await this.getById(userId);
    if (!roleData) return false;
    return roleData.roles.includes(role);
  }

  /**
   * Get all users by role
   */
  async getUsersByRole(role: UserRole): Promise<UserRoleData[]> {
    return this.query([
      ['roles', 'array-contains', role]
    ]);
  }

  /**
   * Get all users (Admin only)
   */
  async getAllUsersWithRoles(limit = 100): Promise<Array<UserRoleData & { email?: string }>> {
    const roles = await this.getAll(limit);
    
    // Enrich with user data
    const enriched = await Promise.all(
      roles.map(async (roleData) => {
        const userDoc = await db.collection(Collections.USERS).doc(roleData.userId).get();
        const userData = userDoc.data();
        
        return {
          ...roleData,
          email: userData?.email
        };
      })
    );
    
    return enriched;
  }
}

// Singleton export
export const rolesService = new RolesService();

